/**
 * CreateEventDates — Step: Schedule (dates, times, timezone, recurrence).
 * Verbatim port of the key fields; recurrence preview panel simplified
 * (full RepeatCalendarPreview ports in a follow-on drop).
 */

import { useEffect, useRef, useState } from "react";
import { alpha, Btn, Card, ChoiceTile, DatePicker, Dropdown, Icon, T, TimeZonePicker, Toggle, tx } from "@v0y4ge/jrni-ui";
import { parseDateString, type DatePickerProps } from "@v0y4ge/jrni-ui";
import * as JUI from "@v0y4ge/jrni-ui";
// composeIsoDateTime + the DatePicker timeZone prop ship in THIS drop's jrni-ui;
// the gate's published type surface lags one release (same class as REQ-007).
// Namespace access + a Partial<DatePickerProps> spread keep the gate green until
// the surface regen — runtime-correct either way; revert to named usage after.
const composeIsoDateTime = (JUI as unknown as Record<string, unknown>).composeIsoDateTime as (d: Date, t?: string | null, tz?: string) => string;
import { CreationShell } from "./CreationShell";
import { CreationStore, useCreationConfig, REPEAT_LABELS_ALL } from "./store";
import { FieldError, FormSection, WizardCanvas } from "./wizard-utils";
import { NumberField } from "../../../lib/NumberField";
import { friendlyDate, friendlyTime, wallClockExistsInTz, resolveEventDayHours, type EventDayHours } from "../../../lib/datetime";
import { useT, t as i18nT } from "../../../lib/i18n";

// ── Repeat helpers ────────────────────────────────────────────────────────────
const REPEAT_KINDS = ["none","weekly","month-date","month-weekday","quarter-date","quarter-weekday","custom"] as const;
function repeatKind(cfg: { repeatIndex?: number }) { return REPEAT_KINDS[cfg.repeatIndex || 0] || "none"; }

/** Date of the Nth `weekday` in a given month. n=1..4 → that occurrence;
 * n>=5 → the LAST such weekday in the month. */
function nthWeekdayOfMonth(year: number, month: number, weekday: number, n: number): Date {
  if (n >= 5) {
    const last = new Date(year, month + 1, 0);
    const shift = (last.getDay() - weekday + 7) % 7;
    return new Date(year, month, last.getDate() - shift);
  }
  const first = new Date(year, month, 1);
  const shift = (weekday - first.getDay() + 7) % 7;
  return new Date(year, month, 1 + shift + (n - 1) * 7);
}

function repeatOccurrences(cfg: Record<string, unknown>, max = 24): Date[] {
  const { date: s } = parseDateString(cfg.start as string || "");
  if (!cfg.start) return [];
  const kind = repeatKind(cfg as { repeatIndex?: number });
  if (kind === "none") return [s];
  const hh = s.getHours(), mm = s.getMinutes();
  const weekday = s.getDay();                   // month-weekday: the weekday to keep
  const weekN = Math.ceil(s.getDate() / 7);     // …and which occurrence of it (1..5 = last)
  const startDay = s.getDate();                 // month-date: the calendar day to keep
  const isMonthly = kind.startsWith("month") || kind.startsWith("quarter");
  const isWeekday = kind.includes("weekday");
  const step = kind.startsWith("quarter") ? 3 : 1;
  const dates: Date[] = [s];
  for (let i = 1; i < max; i++) {
    let next: Date;
    if (kind === "weekly") {
      next = new Date(dates[dates.length - 1]); next.setDate(next.getDate() + 7);
    } else if (isMonthly) {
      const baseMonth = s.getMonth() + i * step;
      const year = s.getFullYear() + Math.floor(baseMonth / 12);
      const month = ((baseMonth % 12) + 12) % 12;
      if (isWeekday) {
        // Nth weekday of the target month (e.g. "3rd Friday") — NOT the same date.
        next = nthWeekdayOfMonth(year, month, weekday, weekN);
      } else {
        // Same calendar day, clamped to the month's length (31st → Feb 28).
        const lastDay = new Date(year, month + 1, 0).getDate();
        next = new Date(year, month, Math.min(startDay, lastDay));
      }
      next.setHours(hh, mm, 0, 0);
    } else break;
    if (cfg.endsMode === "date" && cfg.endsDate) {
      const end = parseDateString(cfg.endsDate as string).date;
      if (next > end) break;
    }
    if (cfg.endsMode === "count" && dates.length >= (cfg.endsCount as number || 1)) break;
    dates.push(next);
  }
  return dates;
}

// ── EndsOnControl ─────────────────────────────────────────────────────────────
function EndsOnControl({ cfg }: { cfg: ReturnType<typeof useCreationConfig> }) {
  const t = useT();
  const mode = (cfg as { endsMode?: string }).endsMode || "never";
  const count = (cfg as { endsCount?: number }).endsCount ?? 1;
  const date = (cfg as { endsDate?: string }).endsDate || "";
  const startFloor = parseDateString((cfg.start as string) || "").date;
  const MODES = [{ v: "never", l: "Never" }, { v: "date", l: "Specific Date" }, { v: "count", l: "After X Occurrences" }];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block" }}>{t("events.creationCreateEventDates.endsOn", "Ends on")}</label>
      <Dropdown
        options={MODES.map((o) => ({ value: o.v, label: o.l }))}
        value={mode}
        onChange={(v) => CreationStore.setField("endsMode", v)}
        aria-label={t("events.creationCreateEventDates.endsOn", "Ends on")}
        testid="ev-ends-mode"
        style={{ gap: 0 }}
      />
      {mode === "date" ? <DatePicker value={date} onChange={(v) => CreationStore.setField("endsDate", v)} leading={<Icon.calendar size={14} />} showTime={false} minDate={startFloor} /> : null}
      {mode === "count" ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <NumberField value={count} onChange={(n) => CreationStore.setField("endsCount", Math.max(1, n))} min={1} ariaLabel={t('events.creationCreateEventDates.ariaLabel')} style={{ width: 90 }} />
          <span style={{ fontSize: 12, color: T.soft }}>{t("events.creationCreateEventDates.occurrences", "occurrences")}</span>
        </div>
      ) : null}
    </div>
  );
}

// ── MonthlyPatternNote ────────────────────────────────────────────────────────
function MonthlyPatternNote({ cfg }: { cfg: ReturnType<typeof useCreationConfig> }) {
  const t = useT();
  const k = repeatKind(cfg as { repeatIndex?: number });
  const { date: s } = parseDateString((cfg.start as string) || "");
  const isWeekday = k.includes("weekday");
  const dayNames = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
  const weekN = Math.ceil(s.getDate() / 7);
  const weekLabel = weekN === 5 ? "last" : `${weekN}${["","st","nd","rd","th"][Math.min(weekN,4)]}`;
  const pattern = isWeekday ? `${weekLabel} ${dayNames[s.getDay()]}` : `the ${s.getDate()}${["","st","nd","rd"][Math.min(s.getDate() % 10,3) * +(s.getDate() > 3 && s.getDate() < 21 ? 0 : 1)] || "th"}`;
  const cadence = k.startsWith("quarter") ? "quarterly" : "monthly";
  return (
    <div style={{ padding: 12, background: T.p4, borderRadius: 6, fontSize: 12, color: T.n1, display: "flex", gap: 8 }}>
      <Icon.info size={13} />
      <span>{t("events.creationCreateEventDates.repeats", "Repeats")}{" "}<strong>{cadence}</strong> {t("events.creationCreateEventDates.on", "on")}{" "}<strong>{pattern}</strong>{t("events.creationCreateEventDates.derivedFromYourStartDate", ", derived from your start date.")}</span>
    </div>
  );
}

// ── CustomInstances ───────────────────────────────────────────────────────────
function CustomInstances({ cfg }: { cfg: ReturnType<typeof useCreationConfig> }) {
  const t = useT();
  const instances = ((cfg as { customInstances?: string[] }).customInstances) || [];
  const [newDate, setNewDate] = useState("");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <label style={{ fontSize: 13, color: T.n1, fontWeight: 500 }}>{t("events.creationCreateEventDates.occurrenceDates", "Occurrence dates")}</label>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {instances.map((d, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: T.n6, borderRadius: 4 }}>
            <Icon.calendar size={13} />
            <span style={{ flex: 1, fontSize: 13, color: T.n1 }}>{d}</span>
            {/* ui-raw: bare transparent inline delete-x inside a compact chip row — IconBtn imposes a bordered 36px chrome box, wrong here */}
            <button type="button" onClick={() => { const next = instances.filter((_,j) => j !== i); CreationStore.setField("customInstances", next); }} style={{ background: "transparent", border: "none", cursor: "pointer", color: T.soft, display: "inline-flex" }}><Icon.x size={12} /></button>
          </div>
        ))}
      </div>
      <DatePicker value={newDate} onChange={(v) => { if (v) { setNewDate(""); CreationStore.setField("customInstances", [...instances, v as string]); } }} leading={<Icon.calendar size={14} />} showTime={false} />
    </div>
  );
}

// ── RepeatPreview (simplified) ────────────────────────────────────────────────
function RepeatPreview({ cfg }: { cfg: ReturnType<typeof useCreationConfig> }) {
  const t = useT();
  const occs = repeatOccurrences(cfg as Record<string, unknown>, 8);
  return (
    <div>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
        <span style={tx.label}>{t("events.creationCreateEventDates.recurrencePreview", "Recurrence preview")}</span>
        <div style={{ fontSize: 11, color: T.soft, marginTop: 2, fontWeight: 400 }}>{t("events.creationCreateEventDates.first", "First")}{" "}{Math.min(occs.length, 8)} {t("events.creationCreateEventDates.occurrences", "occurrences")}</div>
      </div>
      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
        {occs.slice(0, 8).map((d, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
            <span style={{ width: 20, height: 20, borderRadius: 999, background: i === 0 ? T.p1 : T.n6, color: i === 0 ? T.white : T.soft, fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</span>
            <span style={{ color: T.n1 }}>{friendlyDate(d)}</span>
          </div>
        ))}
        {occs.length === 0 ? <div style={{ fontSize: 13, color: T.soft }}>{t("events.creationCreateEventDates.setAStartDateToPreviewOccurrences", "Set a start date to preview occurrences.")}</div> : null}
      </div>
    </div>
  );
}

// ── MultiDayHours ──────────────────────────────────────────────────────────
const timeInputStyle: React.CSSProperties = { width: 108, height: 34, padding: "0 9px", border: `1px solid ${T.n4}`, borderRadius: 6, fontFamily: T.font, fontSize: 12.5, color: T.n1, boxSizing: "border-box" };

/** Per-day hours for a multi-day event. `resolveEventDayHours` is the single
 * source of the day list + defaults (also read by the landing page renderer),
 * so this component only ever WRITES the days the user actually edits — an
 * untouched day keeps tracking the overall start/end time-of-day rather than
 * freezing at whatever that was when the range was first set. */
function MultiDayHours({ cfg }: { cfg: ReturnType<typeof useCreationConfig> }) {
  const t = useT();
  const cfgDH = cfg as { start?: string; end?: string; dayHours?: EventDayHours[] };
  const days = resolveEventDayHours(cfgDH);
  if (days.length < 2) return null;
  const setDayField = (date: string, field: "start" | "end", value: string) => {
    if (!value) return;
    const next = days.map((d) => (d.date === date ? { ...d, [field]: value } : d));
    CreationStore.setField("dayHours", next);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ padding: 10, background: T.p4, borderRadius: 6, fontSize: 12, color: T.n1, display: "flex", gap: 8 }}>
        <Icon.info size={13} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>{t("events.creationCreateEventDates.multiDayHoursHelp", "Multi-day event — set the hours for each day. The landing page lists every day with its own time.")}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {days.map((d, i) => (
          <div key={d.date} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: T.n6, borderRadius: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1, flex: 1, minWidth: 140 }}>{t("events.creationCreateEventDates.day", "Day")} {i + 1} · {friendlyDate(`${d.date}T00:00:00`)}</span>
            {/* ui-raw: native time picker at bespoke compact sizing (34px / 108px / 12.5px) inside a tight inline hours row — Input's wrapper + default 32/40px sizing break the row */}
            <input type="time" value={d.start} onChange={(ev) => setDayField(d.date, "start", ev.target.value)} aria-label={i18nT("events.creationCreateEventDates.startTimeForDayLabel.icu", { value0: i + 1 })} style={timeInputStyle} />
            <span style={{ color: T.soft, fontSize: 12 }}>–</span>
            {/* ui-raw: native time picker at bespoke compact sizing (34px / 108px / 12.5px) inside a tight inline hours row — Input's wrapper + default 32/40px sizing break the row */}
            <input type="time" value={d.end} onChange={(ev) => setDayField(d.date, "end", ev.target.value)} aria-label={i18nT("events.creationCreateEventDates.endTimeForDayLabel.icu", { value0: i + 1 })} style={timeInputStyle} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── CreateEventDates ──────────────────────────────────────────────────────────
export default function CreateEventDates() {
  const t = useT();
  const cfg = useCreationConfig();
  const allDay = !!(cfg as { allDay?: boolean }).allDay;
  const { date: startDate } = parseDateString((cfg.start as string) || "");

  // #724 — the TimeZonePicker shows a default but never commits it unless the
  // user interacts, so the publish payload went out with no tz (the UTC-offset
  // bug). Commit the shown default into the store on mount when unset, so the
  // form value always matches what's displayed.
  const DEFAULT_TZ = "(GMT-5) America/New_York";
  // IANA zone for ISO-offset composition in the pickers (value shape: "(GMT-5) America/New_York").
  const ianaTz = (((cfg as { timezone?: string }).timezone || DEFAULT_TZ).split(" ").pop() || "") as string;
  useEffect(() => {
    if (!(cfg as { timezone?: string }).timezone) CreationStore.setField("timezone", DEFAULT_TZ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAllDay = (next: boolean) => {
    CreationStore.setField("allDay", next);
    // Values are canonical ISO — recompose with/without a time-of-day (00:00 for all-day).
    const withTime = (s: string | undefined, t: string | null) => {
      if (!s) return s;
      const { date } = parseDateString(s);
      return composeIsoDateTime(date, t, ianaTz);
    };
    if (next) {
      CreationStore.setField("start", withTime(cfg.start as string, null));
      CreationStore.setField("end", withTime(cfg.end as string, null));
    } else {
      CreationStore.setField("start", withTime((cfg.start as string) || "", "10:00"));
      CreationStore.setField("end", withTime((cfg.end as string) || "", "12:00"));
    }
  };

  const missingDates: string[] = [];
  if (!cfg.start) missingDates.push("start date");
  if (!cfg.end) missingDates.push("end date");

  // #789.3 — client start<end soft-gate (ui-local pre-submit check; the server
  // 422 EVENT_END_BEFORE_START remains authoritative). parseDateString is the
  // picker's own parse, so the comparison uses exactly the picked wall clock.
  const dtMs = (str: string): number => {
    const { date: d, time: t } = parseDateString(str);
    const x = new Date(d);
    const m = t ? /^(\d{1,2}):(\d{2})/.exec(String(t)) : null;
    if (m) x.setHours(parseInt(m[1], 10), parseInt(m[2], 10), 0, 0);
    return x.getTime();
  };
  // All-day events compose both ends at 00:00 — a same-day all-day event is
  // legitimate, so the ordering gate only applies to timed events.
  const orderBad = !allDay && !!(cfg.start && cfg.end) && dtMs(cfg.start as string) >= dtMs(cfg.end as string);

  // #789.4 — DST spring-forward gap warning (ui-local; server: 422
  // EVENT_LOCAL_TIME_NONEXISTENT). Checks the picked wall clock against the
  // event's own IANA zone.
  const gapAt = (str: unknown): boolean => {
    if (!str || allDay) return false;
    const { date: d, time: t } = parseDateString(String(str));
    const m = t ? /^(\d{1,2}):(\d{2})/.exec(String(t)) : null;
    if (!m) return false; // no picked time → nothing to check
    return !wallClockExistsInTz({ y: d.getFullYear(), mo: d.getMonth(), d: d.getDate(), h: parseInt(m[1], 10), mi: parseInt(m[2], 10) }, ianaTz);
  };
  const startGap = gapAt(cfg.start);
  const endGap = !startGap && gapAt(cfg.end);

  const nextDisabled = missingDates.length > 0 || orderBad || startGap || endGap;
  const nextDisabledReason = missingDates.length > 0 ? `Missing: ${missingDates.join(", ")}`
    : orderBad ? "End must be after start"
    : (startGap || endGap) ? "Pick a time that exists on that date"
    : "";
  const hasRepeat = (cfg.repeatIndex || 0) > 0;
  const kind = repeatKind(cfg as { repeatIndex?: number });

  return (
    <CreationShell step={2} nextDisabled={nextDisabled} nextDisabledReason={nextDisabledReason}>
      <WizardCanvas width="wide" cols={hasRepeat ? "split" : "stack"}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <FormSection title={t("events.creationCreateEventDates.whenDoesTheEventHappen", "When does the event happen?")} subtitle={t("events.creationCreateEventDates.setTheDateSStartEndTimeTimeZoneAndAnyRecurrence", "Set the date(s), start/end time, time zone and any recurrence.")}>
            {/* FormSection stacks its children flush by design, so the step owns the
                field rhythm (Time zone was sitting on the date row — demo report,
                2026-07-30). Same treatment as the Details step. */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <DatePicker label={t("events.creationCreateEventDates.start", "Start *")} value={cfg.start as string | undefined} onChange={(v) => CreationStore.setField("start", v)} leading={<Icon.calendar size={14} />} showTime={!allDay} {...({ timeZone: ianaTz } as unknown as Partial<DatePickerProps>)} />
                <FieldError path="startsAt" />
              </div>
              <div style={{ minWidth: 0 }}>
                <DatePicker label={t("events.creationCreateEventDates.end", "End *")} value={cfg.end as string | undefined} onChange={(v) => CreationStore.setField("end", v)} leading={<Icon.calendar size={14} />} showTime={!allDay} minDate={startDate} {...({ timeZone: ianaTz } as unknown as Partial<DatePickerProps>)} />
                <FieldError path="endsAt" />
              </div>
            </div>
            <TimeZonePicker label={t("events.creationCreateEventDates.timeZone", "Time zone")} value={(cfg as { timezone?: string }).timezone || DEFAULT_TZ} onChange={(v) => CreationStore.setField("timezone", v)} />
            <FieldError path="timezone" />
            {orderBad ? (
              <div role="alert" style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", background: T.orange2, border: `1px solid ${alpha(T.orange1, "55")}`, borderRadius: 8, fontSize: 12.5, color: T.n1, lineHeight: 1.5 }}>
                <span style={{ color: T.orange1, flexShrink: 0, marginTop: 1 }}><Icon.warn size={14} /></span>
                <span><b>{t("events.creationCreateEventDates.theEndIsnTAfterTheStart", "The end isn't after the start.")}</b> {i18nT("events.creationCreateEventDates.thisEventWouldEndBeforeItBeginsAdjustTheEndDateAmp.rendered", "This event would end before it begins — adjust the end date & time to continue.")}</span>
              </div>
            ) : null}
            {(startGap || endGap) ? (
              <div role="alert" style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", background: T.orange2, border: `1px solid ${alpha(T.orange1, "55")}`, borderRadius: 8, fontSize: 12.5, color: T.n1, lineHeight: 1.5 }}>
                <span style={{ color: T.orange1, flexShrink: 0, marginTop: 1 }}><Icon.warn size={14} /></span>
                <span><b>{t("events.creationCreateEventDates.the", "The")}{" "}{startGap ? t('events.creationCreateEventDates.text') : t('events.creationCreateEventDates.text2')}{" "}{t("events.creationCreateEventDates.timeDoesnTExistOnThatDateIn", "time doesn't exist on that date in")}{" "}{ianaTz.replace(/_/g, " ")}.</b> {t("events.creationCreateEventDates.clocksJumpForwardThatNightSo", "Clocks jump forward that night, so")}{" "}{friendlyTime(startGap ? (cfg.start as string) : (cfg.end as string))} {t("events.creationCreateEventDates.isSkippedPickATimeOutsideTheChangeover", "is skipped — pick a time outside the changeover.")}</span>
              </div>
            ) : null}
            {/* a11y: Toggle renders role="switch" (not a labelable input), so a
                wrapping <label> never associated. The label prop puts the text
                inside the switch — accessible name from content, one click target. */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", background: T.n6, borderRadius: 4 }}>
              <Toggle on={allDay} onChange={toggleAllDay} label={<span style={{ fontSize: 13, color: T.n1, fontWeight: 500 }}>{t("events.creationCreateEventDates.allDayEvent", "All-day event")}</span>} />
              <span style={{ fontSize: 12, color: T.soft, marginLeft: "auto" }}>{t("events.creationCreateEventDates.hidesStartEndTimeOnTheLandingPage", "Hides start/end time on the landing page")}</span>
            </div>
            <MultiDayHours cfg={cfg} />
            </div>
          </FormSection>

          <FormSection title={t("events.creationCreateEventDates.repeat", "Repeat")} subtitle={t("events.creationCreateEventDates.makeThisEventRecurWeeklyMonthlyQuarterlyOrACustomSetOf", "Make this event recur — weekly, monthly, quarterly, or a custom set of dates.")}>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
              {REPEAT_LABELS_ALL.map((opt, i) => {
                const active = i === (cfg.repeatIndex || 0);
                return (
                  <ChoiceTile key={opt} label={opt} selected={active} onSelect={() => CreationStore.setField("repeatIndex", i)} showCheck={false} />
                );
              })}
            </div>
            {(cfg.repeatIndex || 0) === 0 ? (
              <div style={{ padding: 12, background: T.n6, borderRadius: 4, fontSize: 12, color: T.soft, display: "flex", alignItems: "center", gap: 8 }}>
                <Icon.info size={12} /> {t("events.creationCreateEventDates.oneOffEventDuplicateFromTheEventsListToSpinUpThe", "One-off event. Duplicate from the events list to spin up the next one.")}</div>
            ) : kind === "custom" ? <CustomInstances cfg={cfg} /> :
              kind === "weekly" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{t("events.creationCreateEventDates.every", "Every")}</label>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d, i) => {
                        const defaultWd = Array.from({ length: 7 }, (_, j) => j === startDate.getDay());
                        const weekdays = (cfg as { weekdays?: boolean[] }).weekdays || defaultWd;
                        const on = !!weekdays[i];
                        return (
                          // ui-raw: bespoke round weekday toggle (fixed 44x36 pill, no padding) — Btn adds horizontal padding that blows out the fixed width
                          <button key={d} type="button" onClick={() => { const next = [...weekdays]; next[i] = !on; if (!next.some(Boolean)) return; CreationStore.setField("weekdays", next); }} style={{ width: 44, height: 36, borderRadius: 999, border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p1 : T.white, color: on ? T.white : T.n1, fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: T.font }} aria-pressed={on}>{d}</button>
                        );
                      })}
                    </div>
                  </div>
                  <EndsOnControl cfg={cfg} />
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <MonthlyPatternNote cfg={cfg} />
                  <EndsOnControl cfg={cfg} />
                </div>
              )}
            </div>
          </FormSection>
        </div>

        {hasRepeat ? (
          <div style={{ position: "sticky", top: 0, display: "flex", flexDirection: "column", gap: 16 }}>
            <Card style={{ padding: 0, overflow: "hidden" }}>
              <RepeatPreview cfg={cfg} />
            </Card>
          </div>
        ) : null}
      </WizardCanvas>
    </CreationShell>
  );
}
